import { useIsAdminUser } from "@mirage/service-auth/useDropboxAccount";
import { useNotificationsLastSeen } from "@mirage/service-dash-trial/hooks/useNotificationsLastSeen";
import { useConnections } from "@mirage/shared/atoms/connections";
import { useAtom } from "jotai";
import { useCallback, useRef } from "react";
import { trialEndingNotificationAtom } from "./atoms";
import { TrialActionType } from "./types";
import { getActionSurface, useLogShownUpsellComponent } from "./utils";

export const TRIGGER_CONNECTOR_UPSELL_AFTER_DAYS_LEFT = 15;
export const DAY_COOLDOWN = 1; // we only want to show the connector upsell once a day

export const useConnectorTrialUpsell = () => {
  const [trialEndingNotification, setTrialEndingNotification] = useAtom(
    trialEndingNotificationAtom,
  );
  const { setLastSeen, lastSeenConnectorNotification } =
    useNotificationsLastSeen();
  const { daysLeft } = trialEndingNotification;
  const showTimeoutRef = useRef<NodeJS.Timeout | undefined>();
  const logShownUpsell = useLogShownUpsellComponent();
  const connections = useConnections();
  const count = connections.data.length;
  const isAdmin = useIsAdminUser();

  const showConnectorTrialNotification = useCallback(
    (daysLeft: number) => {
      setTrialEndingNotification((prev) => ({
        ...prev,
        actionType: TrialActionType.Connector,
        isVisible: true,
      }));
      logShownUpsell({
        actionSurface: getActionSurface(TrialActionType.Connector),
        ctaName: isAdmin ? "view_plans" : "ask_your_admin",
      });
      setLastSeen({ connector: daysLeft });
    },
    [setTrialEndingNotification, setLastSeen],
  );

  const connectorTriggerTrialUpsellCheck = useCallback(() => {
    if (
      daysLeft <= TRIGGER_CONNECTOR_UPSELL_AFTER_DAYS_LEFT &&
      daysLeft <= lastSeenConnectorNotification - DAY_COOLDOWN &&
      count > 2 // user must have already added at least 2 connectors
    ) {
      // The connector flow shows a "connector added" snackbar which auto disappears after
      // 5 seconds, so delay showing the upsell until then.
      clearTimeout(showTimeoutRef.current);
      showTimeoutRef.current = setTimeout(() => {
        showConnectorTrialNotification(daysLeft);
      }, 5000);
    }
  }, [count, lastSeenConnectorNotification, daysLeft]);

  const isTrialNotificationVisible = trialEndingNotification.isVisible;

  const hideTrialNotification = useCallback(() => {
    setTrialEndingNotification((prev) => ({
      ...prev,
      isVisible: false,
    }));
  }, [setTrialEndingNotification]);

  return {
    hideTrialNotification,
    isTrialNotificationVisible,
    connectorTriggerTrialUpsellCheck,
  };
};
